import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCourse } from '../context/CourseContext';
import { useTheme } from '../context/ThemeContext';

const STEADFAST_LOGO = "https://customer-assets.emergentagent.com/job_sfdc-training-ebook/artifacts/5eqwermj_logo.png";
const SALESFORCE_LOGO = "https://customer-assets.emergentagent.com/job_sfdc-training-ebook/artifacts/nr61ks3q_Salesforce.com_logo.png";

export default function WelcomePage() {
  const navigate = useNavigate();
  const { studentName, setStudentName } = useCourse();
  const { theme } = useTheme();
  const [nameInput, setNameInput] = useState('');

  // If name already set, redirect to home
  useEffect(() => {
    if (studentName) {
      navigate('/home', { replace: true });
    }
  }, [studentName, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (nameInput.trim()) {
      setStudentName(nameInput.trim());
      navigate('/home');
    }
  };

  if (studentName) return null;

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="welcome-page">
      {/* Logos bar */}
      <div className="w-full px-6 sm:px-12 lg:px-20 py-6 flex items-center justify-between">
        <img
          src={STEADFAST_LOGO}
          alt="Steadfast IT Consulting"
          className="h-14 sm:h-18 lg:h-20 w-auto object-contain"
          data-testid="welcome-steadfast-logo"
        />
        <img
          src={SALESFORCE_LOGO}
          alt="Salesforce"
          className="h-14 sm:h-18 lg:h-20 w-auto object-contain"
          data-testid="welcome-salesforce-logo"
        />
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="max-w-lg w-full text-center">
          <div className="opacity-0 animate-fade-up">
            <span className="text-xs font-medium text-primary bg-primary/10 px-3 py-1.5 rounded-full uppercase tracking-widest">
              Training Program
            </span>

            <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-foreground mt-6 mb-3">
              Salesforce Administrator
            </h1>
            <h2 className="font-heading font-semibold text-lg sm:text-xl text-muted-foreground mb-4">
              Complete Training Guide
            </h2>

            <p className="text-muted-foreground leading-relaxed mb-10 max-w-md mx-auto">
              A comprehensive program covering 21 modules with 82 topics,
              interactive quizzes, and a certificate of completion.
            </p>
          </div>

          <div className="opacity-0 animate-fade-up animate-delay-200">
            <div className="bg-card border border-border rounded-xl p-6 sm:p-8 shadow-sm">
              <h3 className="font-heading font-semibold text-foreground text-base mb-1">
                Enter Your Name to Begin
              </h3>
              <p className="text-xs text-muted-foreground mb-5">
                This name will appear on your certificate of completion and cannot be changed later.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  placeholder="Your full name"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  className="text-center text-lg h-12"
                  autoFocus
                  data-testid="welcome-name-input"
                />
                <Button
                  type="submit"
                  disabled={!nameInput.trim()}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-11"
                  data-testid="begin-training-btn"
                >
                  Begin Training
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </form>
            </div>
          </div>

          <p className="text-xs text-muted-foreground mt-8 opacity-0 animate-fade-up animate-delay-300">
            Powered by Steadfast IT Consulting
          </p>
        </div>
      </div>
    </div>
  );
}
